import { cn } from "@repo/cn";
import type { SVGIconProps } from "@repo/icons/interface";
import type { FunctionComponent, InputHTMLAttributes } from "react";
import React, { forwardRef } from "react";

export interface CpsInputProps extends InputHTMLAttributes<HTMLInputElement> {
  title?: string;
  errorMessage?: string;
  helpMessage?: string;
  type?: string;
  required?: boolean;
  IconLeft?: FunctionComponent<SVGIconProps>;
  borderColor?: string;
  rounded?: boolean;
  IconRight?: FunctionComponent<SVGIconProps>;
  cursorPointer?: boolean;
  iconRightClassName?: string;
  actionNode?: React.ReactNode;
  /** Callback fired exclusively when the right icon is clicked. When provided,
   * the icon becomes an accessible interactive element (role="button") with its
   * own event handler, independent of the input's onClick. */
  onClickRightIcon?: () => void;
  /** Accessible label for the right icon button. Required when onClickRightIcon
   * is provided so screen readers can announce the action. */
  iconRightAriaLabel?: string;
}

const borderColors: Record<string, string> = {
  primary: "border-primary-50",
  secondary: "border-secondary-50",
  neutral: "border-neutral-200",
};

/**
 * `CpsInput` is a component that renders an input field.
 *
 *
 * @example
 * Here is a basic usage example:
 * ```tsx
 * <CpsInput title="Username" />
 * ```
 * @param title - The title of the input field.
 * @param errorMessage - The error message to be displayed below the input field.
 * @param helpMessage - The help message to be displayed below the input field.
 * @param type - The type of the input field.
 * @param required - Whether the input field is required.
 * @param register - The `register` function from the `useForm` hook.
 * @param IconLeft - The icon to be displayed on the left side of the input field.
 * @param borderColor - The color of the border around the input field.
 * @param rounded - Whether the input field should be rounded.
 * @param IconRight - The icon to be displayed on the right side of the input field.
 * @param cursorPointer - Whether the cursor should be displayed when the input field is focused.
 * @param iconRightClassName - The class name to be applied to the right icon.
 * @param onClickRightIcon - Callback fired exclusively when the right icon is clicked.
 * @param iconRightAriaLabel - Accessible label for the right icon button.
 *
 * @returns The `CpsInput` component.
 */
export const CpsInput = forwardRef<HTMLInputElement, CpsInputProps>(
  (
    {
      title,
      errorMessage,
      helpMessage,
      required,
      type = "text",
      IconLeft,
      borderColor = "neutral",
      rounded = false,
      IconRight,
      iconRightClassName,
      cursorPointer,
      actionNode,
      onClickRightIcon,
      iconRightAriaLabel,
      ...props
    },
    ref,
  ) => {
    const getInputVariationClasses = (): string => {
      const round = rounded ? "rounded-full" : "rounded-md";
      const iconLeftPadding = cn(IconLeft && "pl-12 pr-4");
      const iconRightPadding = cn(IconRight && "pr-12");
      const error = cn(errorMessage && "border-danger-200");
      const hasCursorPointer = cn(cursorPointer && "cursor-pointer");
      const hasTitle = cn(title && "mt-1");

      return cn(
        borderColors[borderColor],
        round,
        error,
        iconLeftPadding,
        iconRightPadding,
        hasCursorPointer,
        hasTitle,
      );
    };

    return (
      <div className="w-full">
        <div className="flex flex-row justify-between">
          {title ? (
            <label
              htmlFor={props.id ?? props.name}
              className={cn("font-medium", actionNode ? "pt-2" : "")}
            >
              {title}
              {required ? "*" : null}
            </label>
          ) : null}
          {actionNode}
        </div>
        <div className="relative">
          {IconLeft ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-4"
            >
              <IconLeft className="fill-neutral-300" size={24} />
            </span>
          ) : null}
          <input
            placeholder={props.placeholder ?? title}
            type={type}
            {...props}
            className={cn(
              "hover:border-primary-300 focus:border-primary-400 focus:ring-primary-200 flex w-full border-2 bg-white p-4 text-sm font-normal text-neutral-600 transition-all duration-200 placeholder:text-neutral-300 focus:outline-hidden focus:ring-3 disabled:bg-neutral-50 disabled:text-neutral-300 disabled:placeholder:text-neutral-300",
              getInputVariationClasses(),
              props.className,
            )}
            ref={ref}
          />
          {IconRight && onClickRightIcon ? (
            <button
              type="button"
              aria-label={iconRightAriaLabel}
              className="absolute right-4 top-4 cursor-pointer"
              onClick={() => onClickRightIcon()}
            >
              <IconRight
                className={cn("fill-neutral-300", iconRightClassName)}
                size={24}
              />
            </button>
          ) : null}
          {IconRight && !onClickRightIcon ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-4 top-4"
            >
              <IconRight
                className={cn("fill-neutral-300", iconRightClassName)}
                size={24}
              />
            </span>
          ) : null}
        </div>
        <span
          className={cn(
            "text-xs",
            errorMessage ? "text-danger-400" : "text-neutral-500",
          )}
        >
          {errorMessage ?? helpMessage}
        </span>
      </div>
    );
  },
);

export default CpsInput;
